/**
 * @lodex-pro/web-portal/routes/Volumes — BASIC+ volume reports.
 *
 * Phase 15 / Task 15.4 (DEC-153) — grouped BBL totals (site/tank/month) with
 * a Recharts BarChart + a table. A plan without volumes renders the PremiumStub instead.
 */

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { usePortalMe, usePortalVolumes } from '../hooks/useOperatorPortal';
import { ExportButtons } from '../components/ExportButtons';
import { PortalDataError } from '../components/PortalDataError';
import { ScopeNotice } from '../components/ScopeNotice';
import { PremiumStub } from './PremiumStub';
import { portalLacks } from '../nav/portal-features';
import { formatNumber } from '@lodex-pro/shared/number-format';

type GroupBy = 'site' | 'tank' | 'month';

export function Volumes(): React.JSX.Element {
  const me = usePortalMe();
  const [groupBy, setGroupBy] = React.useState<GroupBy>('month');
  const { data, isLoading, isError, error } = usePortalVolumes({ groupBy });

  // OPP PR 6 — the plan's OP_VOLUMES feature, as the API gates it.
  if (portalLacks(me.data, 'OP_VOLUMES')) {
    return <PremiumStub feature="volumes" />;
  }

  const groups = data?.groups ?? [];

  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <h1 style={{ margin: 0 }}>Volumes</h1>
        {/* Operator Reports (0241) — this grouping, as a spreadsheet. */}
        <ExportButtons page="volumes" me={me.data} params={{ groupBy }} />
      </div>
      <ScopeNotice me={me.data} />

      <div
        role="tablist"
        aria-label="Group by"
        style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}
      >
        {(['month', 'site', 'tank'] as const).map((g) => (
          <button
            key={g}
            role="tab"
            aria-selected={groupBy === g}
            type="button"
            onClick={(): void => setGroupBy(g)}
            style={{
              padding: '6px 14px',
              background: groupBy === g ? 'var(--color-bg-tertiary)' : 'transparent',
              color: groupBy === g ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: 'var(--radius-pill)',
              cursor: 'pointer',
              fontSize: '13px',
              textTransform: 'capitalize',
            }}
          >
            {g}
          </button>
        ))}
      </div>

      {isLoading && <p role="status">Loading volumes…</p>}
      {isError && <PortalDataError error={error} fallback="Could not load volumes." />}

      {data !== undefined && (
        <>
          <div
            aria-label="Volumes chart"
            style={{
              width: '100%',
              height: '320px',
              background: 'var(--color-bg-secondary)',
              borderRadius: 'var(--radius-card)',
              padding: '12px',
              marginBottom: '16px',
            }}
          >
            {groups.length === 0 ? (
              <p role="status" style={{ color: 'var(--color-text-tertiary)' }}>
                No volume data in this range.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[...groups]}>
                  <XAxis dataKey="label" stroke="var(--color-text-tertiary)" fontSize={12} />
                  <YAxis stroke="var(--color-text-tertiary)" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border-secondary)',
                    }}
                    labelStyle={{ color: 'var(--color-text-primary)' }}
                  />
                  <Bar dataKey="totalBbl" fill="var(--color-accent-blue)" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <table
            aria-label="Volumes table"
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border-primary)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--color-bg-tertiary)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px' }}>Group</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Total BBL</th>
                <th style={{ textAlign: 'right', padding: '8px 12px' }}>Tickets</th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      padding: '16px',
                      textAlign: 'center',
                      color: 'var(--color-text-tertiary)',
                    }}
                  >
                    No data.
                  </td>
                </tr>
              ) : (
                groups.map((g) => (
                  <tr key={g.key} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                    <td style={{ padding: '8px 12px' }}>{g.label}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {formatNumber(g.totalBbl)}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {formatNumber(g.ticketCount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* DEC-302 — Total BBL above is the figure the invoice carries: review
              corrections (DEC-125) are included for the month and site groupings. They
              cannot be for the tank grouping, because a correction is recorded on the
              CAPTURE and has no per-tank share that is not invented (DEC-303). Say which
              basis is on screen rather than leaving an operator to discover that two
              groupings of one window do not add up to the same number. */}
          <p style={{ color: 'var(--color-text-tertiary)', fontSize: '12px', marginTop: '8px' }}>
            {data.reviewedAdjustmentsApplied
              ? 'Totals include volume corrections made in review, so they match the invoice.'
              : 'Totals are gauged volumes. Corrections made in review apply to a whole ticket, not to one tank, so they are not included in the by-tank view — use the by-month or by-site view to match the invoice.'}
          </p>
        </>
      )}
    </main>
  );
}
